import { useRef, useCallback, useEffect, useState } from 'react';
import { GameState, GamePhase, Card as CardData } from '../types';
import { groupHandForDisplay } from '../services/ui';

interface HandDisplayItem {
    key: string;
    topCard: CardData;
    cards: CardData[];
    count: number;
}

interface ScrollState {
    current: number;
    targetSpeed: number;
    currentSpeed: number;
    isDragging: boolean;
    dragStartX: number;
    lastFrameTime: number;
    isTouchScrolling: boolean;
    touchStartX: number;
    touchStartScroll: number;
    lastTouchX: number;
    touchVelocity: number;
}

interface UseHandScrollOptions {
    gameState: GameState | null;
    localPlayerId: number;
    handContainerRef: React.RefObject<HTMLDivElement>;
}

interface UseHandScrollResult {
    handScroll: number;
    setHandScroll: React.Dispatch<React.SetStateAction<number>>;
    updateScrollSpeed: (intensity: number) => void;
    handleBackgroundMouseDown: (e: React.MouseEvent) => void;
    handleBackgroundMouseUp: () => void;
}

export function useHandScroll({
    gameState,
    localPlayerId,
    handContainerRef,
}: UseHandScrollOptions): UseHandScrollResult {
    const [handScroll, setHandScroll] = useState(0);

    const scrollState = useRef<ScrollState>({
        current: 0,
        targetSpeed: 0,
        currentSpeed: 0,
        isDragging: false,
        dragStartX: 0,
        lastFrameTime: 0,
        isTouchScrolling: false,
        touchStartX: 0,
        touchStartScroll: 0,
        lastTouchX: 0,
        touchVelocity: 0,
    });

    const getScrollLimits = useCallback(() => {
        if (!gameState || !gameState.players) return { minScroll: 0, maxScroll: 0 };
        const player = gameState.players.find(p => p.id === localPlayerId);
        if (!player) return { minScroll: 0, maxScroll: 0 };

        const cardSpacing = 110;
        const handLength = groupHandForDisplay(player.hand).length;
        const totalWidth = handLength * cardSpacing;
        const containerWidth = window.innerWidth;

        if (totalWidth > containerWidth) {
            const overflow = totalWidth - containerWidth;
            const padding = 200;
            const maxScroll = (overflow / 2) + padding;
            return { minScroll: -maxScroll, maxScroll: maxScroll };
        } else {
            // If hand is smaller than screen, allow scrolling but keep cards within screen boundaries
            const range = (containerWidth - totalWidth) / 2;
            return { minScroll: -range, maxScroll: range };
        }
    }, [gameState, localPlayerId]);

    const updateScrollSpeed = useCallback((intensity: number) => {
        // intensity: -1.0 to 1.0 (0 = stop)
        // Max speed: ~30px per frame (medium, smooth speed)
        scrollState.current.targetSpeed = intensity * 30;
    }, []);

    // Mouse wheel scrolling handler
    const handleWheelScroll = useCallback((e: WheelEvent) => {
        if (!gameState || gameState.phase !== GamePhase.Preparation) return;

        // Skip if scrolling over a card (let Card handle rotation)
        if ((e.target as HTMLElement).closest('.card-wheel-area')) return;

        // Skip if scrolling over battle log
        if ((e.target as HTMLElement).closest('.battle-log-scroll-area')) return;

        const { minScroll, maxScroll } = getScrollLimits();

        const scrollDelta = e.deltaY * 0.5;
        setHandScroll(prev => {
            const next = prev - scrollDelta;
            return Math.max(minScroll, Math.min(maxScroll, next));
        });
    }, [gameState, getScrollLimits]);

    // Two-finger touch scrolling handlers
    const handleTouchStart = useCallback((e: TouchEvent) => {
        if (!gameState || gameState.phase !== GamePhase.Preparation) return;

        if (e.touches.length === 2) {
            e.preventDefault();
            const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            scrollState.current.isTouchScrolling = true;
            scrollState.current.touchStartX = centerX;
            scrollState.current.touchStartScroll = handScroll;
            scrollState.current.lastTouchX = centerX;
            scrollState.current.touchVelocity = 0;
        }
    }, [gameState, handScroll]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!scrollState.current.isTouchScrolling) return;
        if (e.touches.length !== 2) {
            scrollState.current.isTouchScrolling = false;
            return;
        }

        e.preventDefault();

        const centerX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
        const deltaX = centerX - scrollState.current.touchStartX;

        scrollState.current.touchVelocity = centerX - scrollState.current.lastTouchX;
        scrollState.current.lastTouchX = centerX;

        const { minScroll, maxScroll } = getScrollLimits();

        const newScroll = scrollState.current.touchStartScroll + deltaX;
        setHandScroll(Math.max(minScroll, Math.min(maxScroll, newScroll)));
    }, [getScrollLimits]);

    const handleTouchEnd = useCallback(() => {
        if (!scrollState.current.isTouchScrolling) return;

        if (Math.abs(scrollState.current.touchVelocity) > 2) {
            scrollState.current.targetSpeed = scrollState.current.touchVelocity * 0.3;
        }

        scrollState.current.isTouchScrolling = false;
    }, []);

    // Set up wheel and touch event listeners
    useEffect(() => {
        const container = handContainerRef.current;
        if (!container) return;

        window.addEventListener('wheel', handleWheelScroll, { passive: true });
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        container.addEventListener('touchmove', handleTouchMove, { passive: false });
        container.addEventListener('touchend', handleTouchEnd, { passive: true });

        return () => {
            window.removeEventListener('wheel', handleWheelScroll);
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchmove', handleTouchMove);
            container.removeEventListener('touchend', handleTouchEnd);
        };
    }, [handleWheelScroll, handleTouchStart, handleTouchMove, handleTouchEnd, handContainerRef]);

    // Background press-to-scroll handler
    const handleBackgroundMouseDown = useCallback((e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.card-container')) return;

        scrollState.current.isDragging = true;

        const width = window.innerWidth;
        const centerX = width / 2;
        const clickX = e.clientX;
        const dist = (clickX - centerX) / (width / 2);

        if (Math.abs(dist) < 0.2) {
            updateScrollSpeed(0);
        } else {
            updateScrollSpeed(dist * 0.5);
        }
    }, [updateScrollSpeed]);

    const handleBackgroundMouseUp = useCallback(() => {
        scrollState.current.isDragging = false;
        updateScrollSpeed(0);
    }, [updateScrollSpeed]);

    // Main Scroll Loop
    useEffect(() => {
        let animId: number;

        const loop = (time: number) => {
            const state = scrollState.current;
            const deltaTime = (time - state.lastFrameTime) / 16.67;
            state.lastFrameTime = time;

            state.currentSpeed += (state.targetSpeed - state.currentSpeed) * 0.1 * (deltaTime || 1);

            if (Math.abs(state.currentSpeed) < 0.1) state.currentSpeed = 0;

            if (state.currentSpeed !== 0) {
                setHandScroll(prev => {
                    state.current = prev;

                    const { minScroll, maxScroll } = getScrollLimits();

                    const next = prev + state.currentSpeed;

                    if (next > maxScroll) {
                        state.currentSpeed = 0;
                        return maxScroll;
                    }
                    if (next < minScroll) {
                        state.currentSpeed = 0;
                        return minScroll;
                    }

                    return next;
                });
            }

            animId = requestAnimationFrame(loop);
        };

        animId = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(animId);
    }, [getScrollLimits]);

    return {
        handScroll,
        setHandScroll,
        updateScrollSpeed,
        handleBackgroundMouseDown,
        handleBackgroundMouseUp,
    };
}
